import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onConfirm: (result: File) => void;
};

type Format = 'original' | 'jpeg' | 'png' | 'webp';

const toExt = (fmt: Format, origName: string) => {
  if (fmt === 'original') return origName;
  const base = origName.replace(/\.[^.]+$/,'');
  const ext = fmt === 'jpeg' ? 'jpg' : fmt;
  return `${base}.${ext}`;
};

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function compressImage(file: File, opts: { format: Format; quality: number; maxWidth: number; maxHeight: number }): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    let tw = iw;
    let th = ih;
    if (opts.maxWidth > 0 || opts.maxHeight > 0) {
      const mw = opts.maxWidth > 0 ? opts.maxWidth : iw;
      const mh = opts.maxHeight > 0 ? opts.maxHeight : ih;
      const r = Math.min(mw / iw, mh / ih, 1);
      tw = Math.max(1, Math.floor(iw * r));
      th = Math.max(1, Math.floor(ih * r));
    }
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.drawImage(img, 0, 0, tw, th);
    const mime = opts.format === 'original' ? (file.type || 'image/png') : (opts.format === 'jpeg' ? 'image/jpeg' : `image/${opts.format}`);
    const quality = opts.format === 'png' ? undefined : Math.min(1, Math.max(0.1, opts.quality));
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Compression failed')), mime, quality as any);
    });
    const name = toExt(opts.format, file.name);
    return new File([blob], name, { type: blob.type || mime });
  } finally {
    URL.revokeObjectURL(url);
  }
}

const human = (n: number) => {
  const u = ['B','KB','MB','GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length-1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

const ImageCompressModal: React.FC<Props> = ({ open, file, onClose, onConfirm }) => {
  const [format, setFormat] = useState<Format>('original');
  const [quality, setQuality] = useState(0.8);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [origSize, setOrigSize] = useState<number>(0);
  const [targetSize, setTargetSize] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open || !file) { setPreviewUrl(null); setOrigSize(0); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setOrigSize(file.size);
    return () => URL.revokeObjectURL(url);
  }, [open, file]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!open || !file) { setTargetSize(null); return; }
      try {
        const f = await compressImage(file, { format, quality, maxWidth, maxHeight });
        if (!cancelled) setTargetSize(f.size);
      } catch {
        if (!cancelled) setTargetSize(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, file, format, quality, maxWidth, maxHeight]);

  const onDoConfirm = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const f = await compressImage(file, { format, quality, maxWidth, maxHeight });
      onConfirm(f);
    } finally {
      setProcessing(false);
    }
  };

  if (!open || !file) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="font-semibold text-gray-900">Compress Image</div>
          <button className="text-sm text-gray-600 hover:text-gray-900" onClick={onClose}>Close</button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 aspect-square flex items-center justify-center">
            {previewUrl && (
              <img src={previewUrl} alt={file.name} className="max-h-full max-w-full object-contain" />
            )}
          </div>
          <div className="space-y-3">
            <div className="text-sm text-gray-700">File: <span className="font-medium">{file.name}</span></div>
            <div className="text-sm text-gray-700">Original size: <span className="font-medium">{human(origSize)}</span></div>
            <div className="text-sm text-gray-700">Estimated output: <span className="font-medium">{targetSize ? human(targetSize) : '—'}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-gray-700">Format
                <select className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1" value={format} onChange={e => setFormat(e.target.value as Format)}>
                  <option value="original">Original</option>
                  <option value="jpeg">JPEG</option>
                  <option value="png">PNG</option>
                  <option value="webp">WEBP</option>
                </select>
              </label>
              <label className="text-sm text-gray-700">Quality
                <input type="range" min={0.1} max={1} step={0.05} value={quality} onChange={e => setQuality(parseFloat(e.target.value))} className="mt-2 w-full" disabled={format==='png'} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-gray-700">Max width
                <input type="number" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1" placeholder="0 (no limit)" value={maxWidth || ''} onChange={e => setMaxWidth(Number(e.target.value || 0))} />
              </label>
              <label className="text-sm text-gray-700">Max height
                <input type="number" className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1" placeholder="0 (no limit)" value={maxHeight || ''} onChange={e => setMaxHeight(Number(e.target.value || 0))} />
              </label>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm" onClick={onClose}>Cancel</button>
          <button className="inline-flex items-center justify-center rounded-md bg-gray-900 text-white px-3 py-2 text-sm disabled:opacity-50" onClick={onDoConfirm} disabled={processing}>{processing ? 'Processing...' : 'Use and Upload'}</button>
        </div>
      </div>
    </div>
  );
};

export default ImageCompressModal;
